import React, { ReactElement, forwardRef, useEffect } from 'react';
import {
  useForm,
  SubmitHandler,
  FieldValues,
  UseFormRegister,
  FieldErrorsImpl,
} from 'react-hook-form';
import { FormField, FormProps } from './types';
import {
  Box,
  FormControlLabel,
  FormControlProps,
  inputBaseClasses,
  menuClasses,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import FormControl from '../FormControl';
import Label from '../FormControl/Label';
import { DatePicker } from '@mui/x-date-pickers';

const Form = forwardRef<HTMLFormElement, FormProps>(
  (
    {
      fields,
      styles = {},
      isWithLabelIndent = true,
      onSubmit,
      onIsDirtyChange,
    },
    ref,
  ) => {
    const {
      register,
      handleSubmit,
      formState: { errors, dirtyFields, isDirty },
    } = useForm<FieldValues>();
    console.log({ dirtyFields });

    useEffect(() => {
      if (onIsDirtyChange) {
        console.log({ dirtyFields, isDirty });

        onIsDirtyChange(Object.keys(dirtyFields).length > 0);
      }
    }, [Object.keys(dirtyFields).length, onIsDirtyChange]);

    const renderField = (
      index: number,
      field: FormField,
      register: UseFormRegister<FieldValues>,
      errors: FieldErrorsImpl<FieldValues>,
    ) => {
      let Field: ReactElement<any, any> | null = null;
      let controllerProps: FormControlProps = {};

      switch (field.type) {
        case 'text':
        case 'email':
        case 'tel': {
          Field = (
            <TextField
              defaultValue=""
              value={field.value}
              type={field.type}
              helperText={errors[field.name]?.message as string}
              fullWidth
              InputProps={{
                endAdornment: null,
              }}
              {...register(field.name, field.validation)}
            />
          );
          controllerProps = {
            variant: 'outlined',
          };

          break;
        }
        case 'date': {
          Field = (
            <DatePicker
              defaultValue={field.value}
              {...(register(field.name, field.validation) as any)}
            />
          );
          controllerProps = {
            variant: 'outlined',
          };

          break;
        }
        case 'select': {
          Field = (
            <Select
              defaultValue=""
              error={!!errors[field.name]}
              value={field.value}
              MenuProps={{
                disablePortal: true,
                className: 'select-menu',
                sx: {
                  [`& .${menuClasses.paper}`]: {
                    // WORKAROUND: This is a temporary fix for the select menu positioning for rtl
                    left: '62.875% !important',
                  },
                },
              }}
              {...register(field.name, field.validation)}
            >
              {field.options?.map((option, index) => (
                <MenuItem key={index} value={option.value ?? undefined}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          );
          controllerProps = {
            variant: 'outlined',
            sx: {
              [`& .${inputBaseClasses.input}`]: {
                marginInlineStart: ({ spacing }) => spacing(3),
              },
            },
          };
          break;
        }
        case 'radio': {
          Field = (
            <RadioGroup
              value={field.value}
              {...register(field.name, field.validation)}
            >
              {field.options?.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          );

          controllerProps = {
            component: 'fieldset',
          };
          break;
        }
      }

      return Field ? (
        <Box key={index} display="flex" flexDirection="column">
          <Label indented={isWithLabelIndent}>{field.placeholder}</Label>
          <FormControl {...controllerProps} disabled={field.isDisabled}>
            {Field}
          </FormControl>
        </Box>
      ) : null;
    };

    const handleFormSubmit: SubmitHandler<FieldValues> = (data) =>
      onSubmit(data);

    return (
      <Box
        onSubmit={handleSubmit(handleFormSubmit)}
        component="form"
        sx={styles}
        ref={ref} // Forward ref applied here
      >
        <Stack>
          {fields.map((field, index) =>
            renderField(index, field, register, errors),
          )}
        </Stack>
      </Box>
    );
  },
);

export default Form;
